import FilterTab from './filter-tab';
import PreferenceTypes from '@/store/preference-types';

export default class TabsConfiguration {
    /**
     * Build a configuration object for the grid tabs by combining system tabs (hardcoded) and
     * user tabs (from preferences).
     * @param system {FilterTab[]}
     */
    constructor({
        id, store, systemTabs = [], version = null, filtersVersion = null,
    }) {
        this.store = store;
        this.id = id;
        this.version = version;
        this.filtersVersion = filtersVersion;

        // get stored tabs preferences
        const tabsPreferences = this.getTabsFromPreferences();

        // update current system tabs by stored settings
        // set active value
        const updatedSystemTabs = systemTabs.map((tab, position) => {
            const preference = tabsPreferences.find(t => t.label === tab.label);
            return {
                ...tab,
                position, // set new position value
                offset: preference ? preference.offset : null,
                sortBy: preference ? preference.sortBy || tab.sortBy : null,
                active: preference ? preference.active : false,
                defaultSortBy: tab.sortBy,
            };
        });
        this.tabs = [
            ...TabsConfiguration.processSystemTabs(updatedSystemTabs),
            ...tabsPreferences
                .filter(tab => !tab.systemTab)
                .sort((a, b) => {
                    const aPosition = tabsPreferences.find(t => t.uid === a.uid).position;
                    const bPosition = tabsPreferences.find(t => t.uid === b.uid).position;
                    return aPosition - bPosition;
                }),
        ];
    }

    /**
     * Add a custom tab based on the provided definition object.
     * @param {{}} definition
     */
    addCustomTab(definition) {
        const customTab = new FilterTab(definition);
        this.tabs = [...this.tabs, customTab];
        return customTab;
    }

    /**
     * Delete a tab using its `uid` from the `customTabs` list.
     * @param {FilterTab} targetTab
     */
    deleteCustomTab(targetTab) {
        const index = this.tabs.findIndex(tab => tab.uid === targetTab.uid);
        if (index > -1) {
            this.tabs.splice(index, 1);
        }
    }

    /**
     * Retrieve the current user's preferences and build a set of custom tabs
     * from it. Restore their UID in the process.
     * @returns {FilterTab[]}
     */
    getTabsFromPreferences() {
        const tabPreferences = this.store.getters['user/getUserPreference'](PreferenceTypes.tabs, this.id);

        // return an empty tabs settings if user haven't the saved settings
        if (!tabPreferences || !tabPreferences.tabs) {
            return [];
        }

        const {
            tabs,
            version: preferenceVersion = null,
            // filtersVersion: preferenceFiltersVersion = null,
        } = tabPreferences;

        // TODO Why this.version > preferenceVersion
        // instead of this.version !== preferenceVersion ?
        //
        // TODO ignore tabs if FilterSet was updated?
        // this.filtersVersion !== preferenceFiltersVersion
        // if (this.version !== null
        //     && this.version > preferenceVersion) {
        //     return [];
        // }
        if (this.version !== null && this.version !== preferenceVersion) {
            // if the remote version is inferior to the local
            // one then we return an empty set of tabs to enforce
            // changes done in UI by manual updates
            return [];
        }
        return Object.keys(tabs).map(tabUid => new FilterTab({
            ...tabs[tabUid],
            readOnly: !tabs[tabUid].systemTab, // read only if tab isn't the system tab
            uid: tabUid,
        }));
    }

    /**
     * Build a payload object to define the user's custom tabs for saving
     * in the preferences, and consequently in the user's profile.
     * @returns {*}
     */
    getPreferencesFromTabs() {
        const activeTab = this.getActiveTab();
        const tabs = this.getTabs()
            .reduce((memo, tab, position) => ({
                ...memo,
                [tab.uid]: {
                    position,
                    active: activeTab.uid === tab.uid,
                    readOnly: tab.readOnly,
                    systemTab: tab.systemTab,
                    label: tab.label,
                    filter: tab.filter,
                    offset: tab.offset,
                    sortBy: tab.sortBy,
                    uid: tab.uid,
                },
            }), {});
        return {
            tabs,
            version: this.version,
            filtersVersion: this.filtersVersion,
        };
    }

    /**
     * Save the custom tabs to the preference store and trigger
     * an API update to the profile endpoint.
     * @returns {Promise<void>}
     */
    async saveTabsToPreferences() {
        this.store.dispatch('user/setUserPreference', {
            type: PreferenceTypes.tabs,
            id: this.id,
            data: this.getPreferencesFromTabs(),
        });
    }

    /**
     * Convert a list of system tab definitions into FilterTab instances
     * in read-only mode. System tabs are hardcoded and permanent.
     * @param systemTabs
     * @returns {*}
     */
    static processSystemTabs(systemTabs) {
        return systemTabs.map(tab => new FilterTab({
            ...tab,
            readOnly: true,
            systemTab: true,
        }));
    }

    /**
     * Combine the system tabs and stored tabs (preferences) and
     * return the result for use in the grid.
     * @returns {Array}
     */
    getTabs() {
        return this.tabs;
    }

    getActiveTab() {
        return this.getTabs().find(tab => tab.active);
    }

    setActiveTab(uid) {
        this.getTabs().forEach((tab) => {
            tab.active = (tab.uid === uid);
        });
    }
}
